import { useSelector } from 'react-redux';

import { Box, Card, Icon, LabeledControl, Paragraph, Radio, Select, Tag, ToggleCard } from '@zillow/constellation-10';
import { IconChevronRightOutline, IconWarningFilled } from '@zillow/constellation-icons';

import { RootState } from '../../../store/types';
import { getFormattedPaymentMethod } from '@common/constants/PaymentMethod';
import { RenterPaymentMethodInterface } from '@renter-hub/models/Types';
import { getVerifiedPaymentMethods } from '@common/paymentsUtils/multiplePaymentMethodsUtil';
import { VERIFICATION_STATUS } from '@common/constants/PlaidRequirement';

interface Props {
  isLoading?: boolean;
  onOptionChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onPaymentMethodClick?: (paymentMethod: RenterPaymentMethodInterface) => void;
  selectedPaymentMethodId?: string;
  renderSelect?: boolean;
  renderRadioView?: boolean;
}

const AvailablePaymentMethods = ({
  isLoading,
  onOptionChange,
  onPaymentMethodClick,
  selectedPaymentMethodId,
  renderSelect,
  renderRadioView,
}: Props) => {
  const paymentMethods = useSelector((state: RootState) => state.payments.renter.dashboard.paymentMethods);
  const { enableFastPayments } = useSelector((state: RootState) => state.payments.renter.config);

  const getDisplayPaymentMethods = () => {
    // For radio/select views, only show verified payment methods
    if (renderRadioView || renderSelect) {
      return getVerifiedPaymentMethods(paymentMethods);
    }

    // If fast payments enabled, show all payment methods (including PLAID_REVOKED)
    if (enableFastPayments) {
      return paymentMethods;
    }

    // Otherwise, filter out PLAID_REVOKED status payment methods
    return paymentMethods.filter(
      (paymentMethod) => paymentMethod.paymentMethodStatus !== VERIFICATION_STATUS.PLAID_REVOKED,
    );
  };

  const displayPaymentMethods = getDisplayPaymentMethods();

  if (renderSelect) {
    return (
      <Box>
        <Paragraph textStyle="body-bold">Payment method</Paragraph>
        <Select value={selectedPaymentMethodId} onChange={onOptionChange} disabled={isLoading}>
          {displayPaymentMethods?.map((paymentMethod) => (
            <option key={paymentMethod.id} value={paymentMethod.id}>
              {`${paymentMethod.nickname || paymentMethod.displayName} ....${paymentMethod.accountLast4}`}
            </option>
          ))}
        </Select>
      </Box>
    );
  }

  const getSubText = (paymentMethod: RenterPaymentMethodInterface) => (
    <Box css={{ display: 'flex', flexDirection: 'column' }}>
      <Paragraph textStyle="body-lg-bold">{paymentMethod.nickname || paymentMethod.displayName}</Paragraph>
      <Paragraph css={{ color: 'text.subtle' }} textStyle="body-sm">
        {`${getFormattedPaymentMethod(paymentMethod.accountType)} ....${paymentMethod.accountLast4}`}
      </Paragraph>
    </Box>
  );

  const getTagText = (paymentMethod: RenterPaymentMethodInterface) => {
    if (paymentMethod.paymentMethodStatus === 'pendingAutomaticVerification') {
      return 'Verification In Progress';
    } else if (paymentMethod.paymentMethodStatus === 'pendingManualVerification') {
      return 'Verification needed';
    } else if (paymentMethod.paymentMethodStatus === VERIFICATION_STATUS.PLAID_REVOKED && enableFastPayments) {
      return 'Reconnection required';
    }
    return null;
  };

  const WrapperComponent = renderRadioView ? ToggleCard : Card;
  return (
    <>
      {displayPaymentMethods?.map((paymentMethod) => (
        <WrapperComponent
          interactive={true}
          key={paymentMethod.id}
          elevated={false}
          selected={renderRadioView ? selectedPaymentMethodId === paymentMethod.id : undefined}
          css={{
            padding: 'token(spacing.sm) !important',
            ...(renderRadioView && { marginBottom: 'token(spacing.sm) !important' }),
          }}
          onClick={() => onPaymentMethodClick?.(paymentMethod)}
        >
          {renderRadioView ? (
            <LabeledControl
              label={getSubText(paymentMethod)}
              control={<Radio checked={selectedPaymentMethodId === paymentMethod.id} />}
            />
          ) : (
            <Box
              css={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'token(spacing.xs)',
                flexWrap: 'wrap',
                rowGap: 'token(spacing.xs)',
              }}
            >
              <Box css={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                {getSubText(paymentMethod)}
              </Box>
              <Box css={{ display: 'flex', alignItems: 'center', gap: 'token(spacing.sm)', flexShrink: 0 }}>
                {(paymentMethod.paymentMethodStatus === 'pendingAutomaticVerification' ||
                  paymentMethod.paymentMethodStatus === 'pendingManualVerification' ||
                  (paymentMethod.paymentMethodStatus === VERIFICATION_STATUS.PLAID_REVOKED && enableFastPayments)) && (
                  <Tag
                    icon={<IconWarningFilled />}
                    css={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px', whiteSpace: 'nowrap' }}
                    size="md"
                    tone="warning"
                  >
                    {getTagText(paymentMethod)}
                  </Tag>
                )}
                <Icon size="md" css={{ color: 'brand' }} render={<IconChevronRightOutline />} />
              </Box>
            </Box>
          )}
        </WrapperComponent>
      ))}
    </>
  );
};

export default AvailablePaymentMethods;
